export interface TextChunk {
  seq: number;
  content: string;
}

const DEFAULT_CHUNK_SIZE = 800;
const DEFAULT_OVERLAP = 150;

/**
 * Splits text into overlapping, sentence/paragraph-boundary-aware chunks.
 * Pure and synchronous — no I/O — so it's trivially unit-testable. Overlap
 * keeps context from being lost right at a chunk boundary; preferring a
 * boundary break (over a hard character cut) keeps embeddings meaningful.
 */
export function chunkText(
  text: string,
  chunkSize = DEFAULT_CHUNK_SIZE,
  overlap = DEFAULT_OVERLAP,
): TextChunk[] {
  const normalized = text.replace(/\r\n/g, "\n").trim();
  if (!normalized) return [];

  const chunks: TextChunk[] = [];
  let start = 0;
  let seq = 0;

  while (start < normalized.length) {
    let end = Math.min(start + chunkSize, normalized.length);

    if (end < normalized.length) {
      const window = normalized.slice(start, end);
      const boundary = Math.max(window.lastIndexOf(". "), window.lastIndexOf("\n"));
      if (boundary > chunkSize * 0.5) {
        end = start + boundary + 1;
      }
    }

    const content = normalized.slice(start, end).trim();
    if (content) chunks.push({ seq: seq++, content });
    if (end >= normalized.length) break;
    start = Math.max(end - overlap, start + 1);
  }

  return chunks;
}
